import { useEffect, useSyncExternalStore } from 'react';
import { errorMessage } from '../../../../lib/api';
import { trpc } from '../../../../lib/trpc';
import { useAccess } from '../../../../lib/useAccess';
import { toast } from '../../../../ui';
import { AssignAiDialog } from './AssignAiDialog';

/**
 * „Zleć Claude dla zadania…” spoza karty zadania (kompozycja Fali 5: ⌘K; nazwa z Fali 9). `assignAi.open(taskId)`
 * otwiera dialog „Zleć Claude” dla zadania; `<AssignAiHost />` (montowany raz w `AppShell`) wczytuje zadanie (`tasks.get`, zakres osoby)
 * i pokazuje `AssignAiDialog`. Uprawnienia sprawdza backend (`agents.create`): tu tylko nie otwieramy dialogu dla
 * zadania, którego osoba nie może edytować.
 */
let current: { taskId: string; openedAt: number } | null = null;
const listeners = new Set<() => void>();

function set(next: typeof current): void {
  current = next;
  for (const l of listeners) l();
}

export const assignAi = {
  open(taskId: string): void {
    set({ taskId, openedAt: Date.now() });
  },
  close(): void {
    set(null);
  },
  getSnapshot(): typeof current {
    return current;
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export function AssignAiHost() {
  const access = useAccess();
  const state = useSyncExternalStore(assignAi.subscribe, assignAi.getSnapshot, assignAi.getSnapshot);
  const enabled = state !== null && access.can('agents', 'edit') && access.can('tasks', 'edit');
  const task = trpc.tasks.get.useQuery({ id: state?.taskId ?? '' }, { enabled, retry: false });

  const blocked =
    enabled && task.data
      ? !task.data.canEdit
        ? 'Nie możesz edytować tego zadania.'
        : task.data.archivedAt
          ? 'Zadanie jest w archiwum.'
          : null
      : null;
  useEffect(() => {
    if (!enabled) return;
    if (task.isError) {
      toast.error(errorMessage(task.error, 'Nie udało się wczytać zadania.'));
      assignAi.close();
    } else if (blocked) {
      toast.error(blocked);
      assignAi.close();
    }
  }, [enabled, task.isError, task.error, blocked]);

  if (!enabled || !task.data || blocked) return null;
  return (
    <AssignAiDialog
      key={state.openedAt}
      task={task.data}
      open
      onOpenChange={(open) => {
        if (!open) assignAi.close();
      }}
    />
  );
}
